import React, { useRef, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components' 
import CreatePostBtn from './userProfile/CreatePostBtn'
import useToggleDropdown from 'hooks/useToggleDropdown' 
import NotificationsIcon from '@mui/icons-material/Notifications' 
import { AiOutlineSetting } from "react-icons/ai";
import { GoBell } from "react-icons/go";
import { IoSearchOutline } from "react-icons/io5";
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';

import { toast } from 'react-toastify'

import { getMemberOf, setViewChurchSearchModal } from 'features/memberships/membershipSlice'
 
import { setViewCreatePostModal, postCreateReset } from 'features/posts/postSlice'
import { setPostType } from 'features/createPost/selectPostTypeSlice'
import { setImagePreview, chooseImage } from 'features/settings/profileImageSlice'
import { getNotifications } from 'features/notifs/notifSlice'
import { getProfileChurchData } from 'features/profileChurch/profileChurchSlice'

import { ExtrasList, ExtrasContainer, ExtrasIcon, ExtrasItem } from './userProfile/extras.styled'
import useImageLoader from 'hooks/useImageLoader'
import { genericAvatar } from 'features/constants'


import { HiOutlineChatAlt2 } from "react-icons/hi";
import { useTranslation } from 'react-i18next'


// If you want the church icons instead of MUI, swap these back in.
// import { TiMessages } from "react-icons/ti";
// import { IoSettingsOutline } from "react-icons/io5";
// import { IoMdNotificationsOutline } from "react-icons/io";
import GiveFeedback from 'components/GiveFeedback'

function ExtraSidebar() {
  const { t: translate } = useTranslation()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { churchName } = useParams()

  const ref1 = useRef(null)
  const [isDropped, setIsDropped] = useToggleDropdown(ref1)

  const { user } = useSelector((state) => state.auth)
  const { isPostCreatedSuccess } = useSelector((state) => state.posts)
  const { profileChurch } = useSelector((state) => state.profileChurch)
  const { unreadCount, newMessageNotifications } = useSelector((state) => state.notifications)
  const { getMemberOfLoading, memberOf, getMemberOfSuccess } = useSelector((state) => state.memberships2)

  const isChurch = !!user?.isOrganization

  const { t: navigationT } = useTranslation('navigation')
  const t = useMemo(() => {
    // convenience for language lookups
    return {
      messages: navigationT('tabs.messages'),
      explore: navigationT('feed.explore'),
      settings: translate("web:general.settings"),
      notifications: navigationT('tabs.notifications'),
      myChurch: translate("web:general.myChurch"),
      myChurches: translate("web:general.myChurches"),
      loading: translate("web:general.loading"),
    }
  }, [navigationT, translate])

  // For user profile card image (keeps your user-side image loader behavior)
  const [imageSource] = useImageLoader(
    `${user ? user.imagePath : genericAvatar}`,
    `${user ? user.imagePath : genericAvatar}`
  )

  // notifications on mount (both)
  useEffect(() => {
    dispatch(getNotifications({}))
  }, [dispatch])

  // memberships on mount (users only)
  useEffect(() => {
    if (!isChurch && !getMemberOfLoading) {
      dispatch(getMemberOf())
    }
    // intentionally keep deps minimal to match your current pattern
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // post create success (users + churches? you had it only in UserExtras, but safe to keep for both)
  useEffect(() => {
    if (isPostCreatedSuccess) {
      navigate('/home')
      toast.success(translate('feed:postCreated'))
      dispatch(setViewCreatePostModal(false))
      dispatch(chooseImage(''))
      dispatch(setImagePreview(''))
      dispatch(setPostType(''))
      dispatch(postCreateReset())
    }
  }, [isPostCreatedSuccess, dispatch, navigate])

  const handleMembersDropDown = () => {
    if (getMemberOfSuccess) {
      if (!memberOf || memberOf.length === 0) {
        dispatch(setViewChurchSearchModal())
      } else {
        setIsDropped.toggle(ref1)
      }
    }
  }

  const handleGoToChurch = (username) => {
    if (churchName !== username) {
      dispatch(
        getProfileChurchData({
          profileName: username,
        })
      )
    }
    navigate(`/${username}`)
  }

  // Church card click behavior
  const handleChurchNav = () => {
    if (!user?.name) return

    if (churchName) {
      if (churchName !== user.name) {
        dispatch(
          getProfileChurchData({
            profileName: user.name,
          })
        )
      }
    }
    navigate(`/${user.name}`)
  }

  const displayName = isChurch
    ? user?.fullName
    : (user?.fullName || user?.name) // user-side originally fullName
  const churchDisplayNameForChurchSidebar = isChurch
    ? user?.fullName
    : profileChurch?.fullName

  return (
    <ExtrasContainer>
      {/* Profile Card */}
      {isChurch ? (
        <ProfileImageSidePaneChurch onClick={handleChurchNav}>
          <PITop>
            <PCImage src={user?.imagePath} alt="u" />
          </PITop>

          <PIBottom>
            <PITopNameChurch>{churchDisplayNameForChurchSidebar}</PITopNameChurch>
          </PIBottom>
        </ProfileImageSidePaneChurch>
      ) : (
        <ProfileImageSidePaneUser>
          <PITop>
            <img
              src={imageSource}
              alt=""
              style={{
                width: '35px',
                height: '35px',
                borderRadius: '50%',
                objectFit: 'cover',
                marginRight: '15px',
              }}
            />
            <PITopNameUser>{displayName}</PITopNameUser>
          </PITop>
        </ProfileImageSidePaneUser>
      )}

      {!isChurch && (
        <MemberOfContainer> 
          <MemberOfTitle>{t.myChurch}</MemberOfTitle> 
          {
            (getMemberOfSuccess && memberOf.length > 0 ) ?
            (
                <>
                    {memberOf.map((ele) => (
                        <MemberOfItem key={ele?._id || ele?.username} onClick={() => handleGoToChurch(ele.username)}>
                        <img
                            src={`${ele.profileImg?.url} `}
                            alt=""
                            style={{
                            width: '35px',
                            height: '35px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            marginRight: '15px',
                            }}
                        />
                        <span
                            style={{
                            paddingLeft: '0px',
                            fontSize: '11px',
                            }}
                        >
                            {ele.fullName}
                        </span>
                        </MemberOfItem>
                    ))}
                </>
            ) : getMemberOfLoading ? (

                <span style={{ fontSize: '11px', color: 'var(--lightThemeFontColor)' }}>{translate('common:loading')}</span>

            ) : (

                <FindChurchBtn onClick={() => dispatch(setViewChurchSearchModal())}>
                    <IoSearchOutline style={{ fontSize: '15px', flexShrink: 0 }} />
                    <span>{navigationT('moreMenu.findMyChurch')}</span>
                </FindChurchBtn>

            )}
        </MemberOfContainer>
      )}

      {/* Messages (both) */}
      <ExtrasList onClick={() => navigate(`/messages`)}>
        <ExtrasIcon>
          <HiOutlineChatAlt2 />
          {newMessageNotifications && newMessageNotifications.length > 0 && <NotifNumber>{newMessageNotifications.length}</NotifNumber>}
        </ExtrasIcon>
        <ExtrasItem>{t.messages}</ExtrasItem>
      </ExtrasList>

      {/* Settings (conditional route) */}
      <ExtrasList
        onClick={() =>
          navigate(isChurch ? `/settings?section=addImage` : `/settings?section=memberships`)
        }
      >
        <ExtrasIcon>
          <AiOutlineSetting />
        </ExtrasIcon>
        <ExtrasItem>{t.settings}</ExtrasItem>
      </ExtrasList>

      {/* Explore (both) */}
      <ExtrasList onClick={() => navigate('/explore')}>
        <ExtrasIcon>
          <ExploreOutlinedIcon />
        </ExtrasIcon>
        <ExtrasItem>{t.explore}</ExtrasItem>
      </ExtrasList>

      {/* Notifications (both) */}
      <ExtrasList onClick={() => navigate(`/notifications`)}>
        <ExtrasIcon>
          <GoBell />
          {unreadCount > 0 && <NotifNumber>{unreadCount}</NotifNumber>}
        </ExtrasIcon>
        <ExtrasItem>{t.notifications}</ExtrasItem>
      </ExtrasList>

      {/* Create Post (both) */}
      <CreatePostBtn />

      {/* Church-only: Feedback */}
      {isChurch && <GiveFeedback />}
    </ExtrasContainer>
  )
}

export default ExtraSidebar

// ---------------- styled ----------------
const MemberOfTitle = styled.div`   
    font-family: 'Roboto', sans-serif;
    color: var(--lightThemeFontColor);  
    font-size: 11px;
    margin-bottom: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-left: 5px;
`

const MemberOfContainer = styled.div`
    position: relative;   
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: #f5f5f5;
    border-radius: 10px;
    margin-top: 10px;
    padding: 10px;
    box-shadow: 0px 1px 4px #c4c4c4;
`

const MemberOfItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  transition: 0.3s;
  border-radius: 7px;

  span {
    font-size: 12px;
    font-family: 'Roboto', sans-serif;
    color: var(--lightThemeFontColor);

    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :hover {
    background-color: #e1e1e1;

    span {
      text-decoration: underline;
    }
  }
`

// User profile card styles (keeps your user component look)
const ProfileImageSidePaneUser = styled.div`
  width: 100%;
  margin-top: 30px;
  display: flex;
  flex-direction: column;

  img {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: #c7c7c7;
  }
`

const PITopNameUser = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: 10px;
  font-family: 'Roboto', sans-serif;
  color: var(--lightThemeFontColor);
`

// Church profile card styles (keeps your church component look)
const ProfileImageSidePaneChurch = styled.div`
  width: 100%;
  margin-top: 20px;
  display: flex;
  flex-direction: row;
  border-radius: 10px;
  padding: 10px;
  align-items: center;
`

const PCImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: grey;
  margin-bottom: 10px;
`

const PIBottom = styled.div`
  display: flex;
  flex-direction: column;
`

const PITop = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const PITopNameChurch = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: 10px;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: var(--lightThemeFontColor);
`

const FindChurchBtn = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1.5px dashed var(--themeColor);
    background-color: rgba(0, 180, 204, 0.05);
    cursor: pointer;
    color: var(--themeColor);
    font-size: 12px;
    font-weight: 600;
    transition: background 0.15s;

    &:hover {
        background-color: rgba(0, 180, 204, 0.12);
    }
`

const NotifNumber = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: red;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: smaller;
`
